import type { MessageFormatElement } from 'react-intl';
import { BreadcrumbItem } from 'components/breadcrumb/breadcrumbModel';

export function buildSegmentsFromPath(pathname: string): string[] {
  return pathname.split('/').filter(segment => segment !== '');
}

export function buildPathFromSegments(segments: string[]): string {
  return `/${segments.join('/')}`;
}

export function createSectionBreadcrumb(sectionSegments: string[], compoundRules: string[]): BreadcrumbItem {
  const [sectionType, sectionValue] = sectionSegments;
  const sectionPath = sectionSegments.join('/');

  const sectionBreadcrumb: BreadcrumbItem = { path: sectionPath };
  const isCompoundRule = compoundRules.includes(sectionType);

  if (isCompoundRule && sectionValue) {
    sectionBreadcrumb.label = sectionValue;
  }

  return sectionBreadcrumb;
}

export function generateBreadcrumbLabel(
  slug: string,
  messages: Record<string, string> | Record<string, MessageFormatElement[]>
): { messageId: string; label: string; hasMessage: boolean } {
  const pathSegments = slug
    .split('/')
    .filter(segment => segment !== '')
    .map(segment => decodeURIComponent(segment));

  if (pathSegments.length >= 1) {
    const hierarchicalMessageId = pathSegments.join('.').replace(/-/g, '_');

    if (messages[hierarchicalMessageId]) {
      return {
        messageId: hierarchicalMessageId,
        label: String(messages[hierarchicalMessageId]),
        hasMessage: true,
      };
    }

    const lastSegment = pathSegments[pathSegments.length - 1];

    const fallbackLabel = lastSegment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return {
      messageId: hierarchicalMessageId,
      label: fallbackLabel,
      hasMessage: false,
    };
  }

  return {
    messageId: slug,
    label: slug,
    hasMessage: false,
  };
}

export function isPathChild(childPath: string, parentPath: string): boolean {
  if (childPath === parentPath) return false;
  return childPath.startsWith(`${parentPath}/`);
}

export function makePathRelative(absolutePath: string, parentPath: string): string {
  if (!isPathChild(absolutePath, parentPath)) {
    return absolutePath;
  }
  return absolutePath.slice(parentPath.length + 1);
}

export function optimizeBreadcrumbPaths(breadcrumbs: BreadcrumbItem[]): BreadcrumbItem[] {
  if (breadcrumbs.length === 0) return [];

  let previousFullPath = '';

  return breadcrumbs.reduce<BreadcrumbItem[]>((acc, breadcrumb) => {
    const currentFullPath = breadcrumb.path;

    if (isPathChild(currentFullPath, previousFullPath)) {
      const relativePath = makePathRelative(currentFullPath, previousFullPath);
      acc.push({
        ...breadcrumb,
        path: relativePath,
      });
    } else {
      acc.push(breadcrumb);
    }

    previousFullPath = currentFullPath;

    return acc;
  }, []);
}

export function getLastPathSegment(path: string, compoundRules: string[]): string {
  const segments = path.split('/').filter(s => s !== '');
  if (segments.length === 0) return '';

  const lastSegment = segments[segments.length - 1];

  if (segments.length > 1) {
    const secondToLast = segments[segments.length - 2];
    if (compoundRules.includes(secondToLast)) {
      return secondToLast;
    }
  }

  return lastSegment;
}

export function getParentPath(path: string): string {
  const segments = path.split('/').filter(s => s !== '');
  if (segments.length <= 1) return '';
  return `/${segments.slice(0, -1).join('/')}`;
}

export function applyNaturalExceptions(
  breadcrumbs: BreadcrumbItem[],
  exceptions: { target: string; successor: string }[],
  compoundRules: readonly string[]
): BreadcrumbItem[] {
  if (breadcrumbs.length === 0 || exceptions.length === 0) {
    return breadcrumbs;
  }

  const rulesToApply = breadcrumbs
    .map((breadcrumb, index) => {
      const lastSegment = getLastPathSegment(breadcrumb.path, compoundRules as string[]);
      const rule = exceptions.find(r => r.target === lastSegment) ?? null;

      return {
        breadcrumb,
        index,
        rule,
      };
    })
    .filter(item => item.rule !== null);

  if (rulesToApply.length === 0) {
    return breadcrumbs;
  }

  const result = [...breadcrumbs];

  rulesToApply.reverse().forEach(({ index, rule }) => {
    const targetBreadcrumb = result[index];
    const targetParent = getParentPath(targetBreadcrumb.path);

    const successorIndex = result.findIndex(bc => {
      const bcParent = getParentPath(bc.path);
      const bcLastSegment = getLastPathSegment(bc.path, compoundRules as string[]);
      return bcParent === targetParent && bcLastSegment === rule?.successor;
    });

    if (successorIndex >= 0 && successorIndex < index) {
      const [successor] = result.splice(successorIndex, 1);
      result.splice(index, 0, successor);
    }
  });

  return result;
}
